import { Action, Reducer } from 'redux';
import {
  ACTION, RequestCases, ReceiveCases, RequestRemoveItem, ReceiveRemoveItem, FailRemoveItem,
  ReceivePermissions, ReceiveUiDefinitions, ClearResults, ReceiveRiskMatrixDefinitions, FailExecuteFavourite,
  RequestUiDefinitions, SetRenderMode, ClearInstance, AddInstance, RequestLayoutTypes, ResponseLayoutTypes,
  RequestDefaultIncludedFields,
  ResponseDefaultIncludedFields,
  ReceiveLayoutModel
} from './action-types';
import { CaseListingInstanceState, CaseListingStoreState } from './types-store';
import { RenderMode } from 'app/types-shared';
import uniqBy from 'lodash/uniqBy';
import { EXECUTE_STATUS } from 'app/area/reports/containers/chart/types-store';

export const INITIAL_CASE_LISTING_INSTANCE_STATE: CaseListingInstanceState = {
  renderMode: RenderMode.report,
  fetching: false,
  requestId: 0,
  executeStatus: EXECUTE_STATUS.IDLE,
  totalNumberOfResults: undefined,
  entities: undefined,
  permissions: undefined,
  uiDefinitions: undefined,
  riskMatrixDefinitions: undefined,
  results: undefined,
  errorsExecuteFailed: undefined,
  layoutTypes: undefined,
  defaultFields: undefined,
  additionalIncludedFields: undefined,
  layoutModel: undefined
};

export const INITIAL_CASE_LISTING_STATE: CaseListingStoreState = {
  instances: {},
  texts: {} as any
};

function transform(
  state: CaseListingStoreState,
  instance: string,
  requestId: number | undefined,
  transformation: (caseListing: CaseListingInstanceState) => CaseListingInstanceState): CaseListingStoreState {
  return {
    ...state,
    instances: Object.keys(state.instances).reduce((aggr, key) => {
      if (key === instance && (!requestId || state.instances[key].requestId === requestId)) {
        aggr[key] = transformation(state.instances[key]);
      }
      else {
        aggr[key] = state.instances[key];
      }
      return aggr;
    }, {})
  };
}

const reducers: { [indexer: string]: Reducer<CaseListingStoreState, Action> } = {

  [ACTION.CLEAR_RESULT]: (state: CaseListingStoreState, action: ClearResults): CaseListingStoreState => {
    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        ...INITIAL_CASE_LISTING_INSTANCE_STATE,
        caseListingInstanceKey: action.instanceKey,
        requestId: i.requestId + 1,
        renderMode: i.renderMode,
        layoutTypes: i?.layoutTypes,
        defaultFields: i?.defaultFields,
        additionalIncludedFields: i?.additionalIncludedFields
      })
    );
  },

  [ACTION.RECEIVE_PERMISSIONS]: (state: CaseListingStoreState, action: ReceivePermissions): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => {
        return {
          ...i,
          permissions: {
            ...(i.permissions ?? {}),
            ...action.permissions
          }
        };
      }
    );
  },
  [ACTION.REQUEST_UIDEFINITIONS]: (state: CaseListingStoreState, action: RequestUiDefinitions): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => ({
        ...i,
        uiDefinitions: [
          ...(i.uiDefinitions ?? []).filter(
            ui => !action.uiDefinitions.some(a => ui.caseTypeId === a.caseTypeId && ui.caseContextId === a.caseContextId)
          ),
          ...action.uiDefinitions.map(ui => ({
            ...ui,
            fetching: true
          }))
        ]
      })
    );
  },
  [ACTION.RECEIVE_UIDEFINITIONS]: (state: CaseListingStoreState, action: ReceiveUiDefinitions): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => ({
        ...i,
        uiDefinitions: (i.uiDefinitions ?? []).map(existingDefinition => {
          const receivedDefinition = action.uiDefinitions.find(
            u => existingDefinition.caseTypeId === u.caseTypeId && existingDefinition.caseContextId === u.caseContextId
          );
          return receivedDefinition ?
            { ...receivedDefinition, fetching: false } :
            existingDefinition;
        })
      })
    );
  },

  [ACTION.RECEIVE_RISKMATRIXDEFINITIONS]: (state: CaseListingStoreState, action: ReceiveRiskMatrixDefinitions): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => ({
        ...i,
        riskMatrixDefinitions: {
          ...(i.riskMatrixDefinitions ?? {}),
          ...action.riskMatrixDefinitions
        }
      })
    );
  },

  /**
   * Run when starting fetching data for a case report favourite.
   */
  [ACTION.REQUEST_CASES]: (state: CaseListingStoreState, action: RequestCases): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        fetching: true,
        executeStatus: EXECUTE_STATUS.PENDING,
        requestId: i.requestId + 1
      })
    );
  },

  /**
   * Run when finished fetching data for case report favourite.
   */
  [ACTION.RECEIVE_CASES]: (state: CaseListingStoreState, action: ReceiveCases): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => ({
        ...i,
        entities: {
          ...(i.entities || {}),
          ...action.entities
        },
        results: i.results ? i.results.concat(action.caseResults) : [...action.caseResults],
        fetching: false,
        executeStatus: EXECUTE_STATUS.SUCCEEDED,
        totalNumberOfResults: action.totalNumberOfResults,
        errorsExecuteFailed: undefined,
        layoutTypes: i?.layoutTypes
      })
    );
  },

  [ACTION.REPLACE_CASES]: (state: CaseListingStoreState, action: ReceiveCases): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      action.requestId,
      i => ({
        ...i,
        entities: action.entities,
        results: action.caseResults,
        fetching: false,
        executeStatus: EXECUTE_STATUS.SUCCEEDED,
        totalNumberOfResults: action.totalNumberOfResults,
        errorsExecuteFailed: undefined,
        layoutTypes: i?.layoutTypes
      })
    );
  },

  [ACTION.REQUEST_REMOVE_ITEM]: (state: CaseListingStoreState, action: RequestRemoveItem): CaseListingStoreState => {
    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        results: i.results.map(item => item.caseNumber !== action.itemId ? item : {
          ...item,
          deleted: true
        }),
        totalNumberOfResults: i.totalNumberOfResults - 1
      })
    );
  },

  [ACTION.RECEIVE_REMOVE_ITEM]: (state: CaseListingStoreState, action: ReceiveRemoveItem): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        results: i.results.filter(item => !item.deleted || item.caseNumber !== action.itemId)
      })
    );
  },

  [ACTION.FAIL_REMOVE_ITEM]: (state: CaseListingStoreState, action: FailRemoveItem): CaseListingStoreState => {

    return transform(
      state,
      action.instance,
      undefined,
      i => ({
        ...i,
        results: i.results.map(item => item.caseNumber !== action.itemId ? item : {
          ...item,
          deleted: false
        }),
        totalNumberOfResults: i.totalNumberOfResults + 1
      })
    );
  },

  [ACTION.FAIL_EXECUTE_FAVOURITE]: (state: CaseListingStoreState, action: FailExecuteFavourite): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        fetching: false,
        executeStatus: EXECUTE_STATUS.FAILED,
        errorsExecuteFailed: action.errorsExecuteFailed
      })
    );
  },

  [ACTION.SET_RENDERMODE]: (state: CaseListingStoreState, action: SetRenderMode): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        renderMode: action.renderMode
      })
    );
  },

  [ACTION.ADD_INSTANCE]: (state: CaseListingStoreState, action: AddInstance): CaseListingStoreState => {
    return {
      ...state,
      instances: {
        ...state.instances,
        [action.instanceKey]: {
          ...INITIAL_CASE_LISTING_INSTANCE_STATE
        }
      }
    };
  },

  [ACTION.CLEAR_INSTANCE]: (state: CaseListingStoreState, action: ClearInstance): CaseListingStoreState => {

    return {
      ...state,
      instances: Object.keys(state?.instances).reduce(
        (aggr, key) => {
          if (key !== action.instanceKey) {
            aggr[key] = state.instances[key];
          }
          return aggr;
        }, {}
      )
    };
  },

  [ACTION.REQUEST_LAYOUT_TYPES]: (state: CaseListingStoreState,
    action: RequestLayoutTypes
  ): CaseListingStoreState => {
    return {
      ...state
    };
  },

  [ACTION.RESPONSE_LAYOUT_TYPES]: (state: CaseListingStoreState,
    action: ResponseLayoutTypes
  ): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        layoutTypes: action.data
      })
    );
  },

  [ACTION.RECEIVE_LAYOUT_MODEL]: (state: CaseListingStoreState,
    action: ReceiveLayoutModel
  ): CaseListingStoreState => {

    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        layoutModel: action.layoutModel
      })
    );
  },

  [ACTION.REQUEST_DEFAULT_INCLUDED_FIELDS]: (state: CaseListingStoreState,
    action: RequestDefaultIncludedFields
  ): CaseListingStoreState => {
    return {
      ...state
    };
  },

  [ACTION.RESPONSE_DEFAULT_INCLUDED_FIELDS]: (state: CaseListingStoreState,
    action: ResponseDefaultIncludedFields
  ): CaseListingStoreState => {
    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        defaultFields: action.data
      })
    );
  },

  [ACTION.REQUEST_ADDITIONAL_FIELDS]: (state: CaseListingStoreState,
    action: RequestDefaultIncludedFields
  ): CaseListingStoreState => {
    return {
      ...state
    };
  },

  [ACTION.RESPONSE_ADDITIONAL_FIELDS]: (state: CaseListingStoreState,
    action: ResponseDefaultIncludedFields
  ): CaseListingStoreState => {
    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        additionalIncludedFields: action.data
      })
    );
  },

  [ACTION.UPDATE_DEFAULT_INCLUDED_FIELDS]: (state: CaseListingStoreState,
    action: ResponseDefaultIncludedFields
  ): CaseListingStoreState => {
    return transform(
      state,
      action.instanceKey,
      undefined,
      i => ({
        ...i,
        defaultFields: uniqBy(i.defaultFields.concat(action.data), 'value')
      })
    );
  }
};


export const reducer: Reducer<CaseListingStoreState> = (state: CaseListingStoreState = INITIAL_CASE_LISTING_STATE, action: any) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};
